// Shared mastery aggregation. Extracted from MasteryDashboard so the dashboard
// tile and the dedicated Learning Insights page (§5.2.5) compute concept mastery
// the same way.

import type { LearningPath, CheckpointScore } from '@shared/types/learning-path';
import type { ContentDocument } from '@shared/types/content';

export interface ScoredConcept {
  conceptId: string;
  name: string;
  score: number;
  timestamp: string;
}

export interface VelocitySummary {
  thisWeek: number;
  lastWeek: number;
  trend: 'up' | 'down' | 'flat';
}

// Collapses every checkpoint score across a user's paths into a per-concept
// average, resolving concept names from the source content documents.
export function aggregateConcepts(
  paths: LearningPath[],
  content: ContentDocument[],
): ScoredConcept[] {
  const byConceptId = new Map<string, { name: string; scores: number[]; latestTs: string }>();

  for (const path of paths) {
    const scores = path.progress?.checkpointScores ?? {};
    const contentDoc = content.find((c) => c.contentId === path.contentId);
    const concepts = ((contentDoc?.concepts ?? []) as unknown) as Array<{
      concept_id?: string;
      conceptId?: string;
      name: string;
    }>;

    for (const [nodeId, cp] of Object.entries(scores as Record<string, CheckpointScore>)) {
      const node = path.nodes?.find((n) => n.nodeId === nodeId);
      if (!node) continue;
      const ts = cp.timestamp ?? path.lastAccessedAt ?? path.createdAt;
      for (const cId of node.conceptIds ?? []) {
        const conceptMeta = concepts.find((c) => (c.concept_id ?? c.conceptId) === cId);
        const name = conceptMeta?.name ?? cId;
        const existing = byConceptId.get(cId);
        if (existing) {
          existing.scores.push(cp.score);
          if (ts > existing.latestTs) existing.latestTs = ts;
        } else {
          byConceptId.set(cId, { name, scores: [cp.score], latestTs: ts });
        }
      }
    }
  }

  return Array.from(byConceptId.entries()).map(([conceptId, data]) => ({
    conceptId,
    name: data.name,
    score: data.scores.reduce((a, b) => a + b, 0) / data.scores.length,
    timestamp: data.latestTs,
  }));
}

// Concepts crossing the mastery threshold this week vs last week.
export function weeklyVelocity(concepts: ScoredConcept[]): VelocitySummary {
  const now = Date.now();
  const weekMs = 7 * 24 * 60 * 60 * 1000;
  let thisWeek = 0;
  let lastWeek = 0;
  for (const c of concepts) {
    if (c.score < 80) continue;
    const ts = new Date(c.timestamp).getTime();
    if (Number.isNaN(ts)) continue;
    const age = now - ts;
    if (age <= weekMs) thisWeek++;
    else if (age <= weekMs * 2) lastWeek++;
  }
  const trend = thisWeek > lastWeek ? 'up' : thisWeek < lastWeek ? 'down' : 'flat';
  return { thisWeek, lastWeek, trend };
}
